import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../helpers/language-context';
import { renderHTML } from '../../helpers/render-html';

const Projects = () => {
    const { translations } = useLanguage();
    const [hoveredCard, setHoveredCard] = useState(null);
    const [isInView, setIsInView] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsInView(true);
                    observer.unobserve(entry.target);
                }
            },
            {
                threshold: 0,
                rootMargin: '0px'
            }
        );

        const currentSectionRef = sectionRef.current;
        if (currentSectionRef) {
            observer.observe(currentSectionRef);
        }

        return () => {
            if (currentSectionRef) {
                observer.unobserve(currentSectionRef);
            }
        };
    }, []);

    const projects = [
        {
            id: 1,
            title: translations.projects.horas,
            description: translations.projects.horasDesc,
            image: '/assets/images/projects/copper_mine.jpg'
        },
        {
            id: 2,
            title: translations.projects.smartDocket,
            description: translations.projects.smartDocketDesc,
            image: '/assets/images/services/smart_docket_new.jpg'
        },
        {
            id: 3,
            title: translations.projects.turapanMega,
            description: translations.projects.turapanMegaDesc,
            image: '/assets/images/projects/turapan_mega.jpg'
        }
    ];

    return (
        <section ref={sectionRef} className="bg-gradient-to-br from-slate-50 to-slate-100 overflow-hidden">
            <div className="mx-auto">
                {/* Header */}
                <div className={`flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 sm:mb-12 transition-all duration-1000 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}>
                    <h1 className="text-3xl md:text-5xl text-[#28357B] leading-tight mb-4 sm:mb-0" dangerouslySetInnerHTML={renderHTML(translations.projects.featuredProjects)} />
                    <button className="hidden bg-[#28357B] hover:bg-[#28357B]/90 text-white px-6 py-3 rounded-full flex items-center gap-2 transition-all">
                        Explore More
                        <svg
                            className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1"
                            fill="none"
                            viewBox="0 0 14 10"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="1.5"
                                d="M1 5h12m0 0L9 1m4 4L9 9"
                            />
                        </svg>
                    </button>
                </div>

                {/* Cards Container */}
                <div className="flex flex-col lg:flex-row gap-6 h-[70vh] min-h-[600px] lg:h-[500px]">
                    {projects.map((project, index) => {
                        const isHovered = hoveredCard === project.id;
                        const isAnyHovered = hoveredCard !== null;

                        return (
                            <div
                                key={project.id}
                                onMouseEnter={() => setHoveredCard(project.id)}
                                onMouseLeave={() => setHoveredCard(null)}
                                style={{ transitionDelay: isInView ? `${index * 150}ms` : '0ms' }}
                                className={`relative rounded-3xl overflow-hidden cursor-pointer transition-all duration-700 ease-out ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
                                    } ${isAnyHovered
                                        ? isHovered
                                            ? 'flex-[2]'
                                            : 'flex-[1]'
                                        : 'flex-1'
                                    }`}
                            >
                                {/* Background Image */}
                                <div
                                    className="absolute inset-0 bg-cover bg-center transition-transform duration-700"
                                    style={{
                                        backgroundImage: `url(${project.image})`,
                                        transform: isHovered ? 'scale(1.1)' : 'scale(1)'
                                    }}
                                />

                                {/* Overlay */}
                                <div className={`absolute inset-0 bg-gradient-to-t transition-all duration-500 ${isHovered
                                    ? 'from-black/80 via-black/50 to-transparent'
                                    : 'from-black/60 via-black/30 to-transparent'
                                    }`} />

                                {/* Content */}
                                <div className="relative h-full flex flex-col justify-end p-8">
                                    <h2 className={`text-white font-bold mb-2 transition-all duration-500 ${isHovered ? 'text-3xl md:text-4xl' : 'text-xl md:text-2xl'
                                        }`}>
                                        {project.title}
                                    </h2>
                                    <p className={`text-white/90 text-sm transition-all duration-500 ${isHovered ? 'opacity-100 max-h-40' : 'opacity-0 max-h-0'
                                        }`}>
                                        {project.description}
                                    </p>
                                </div>

                                {/* Hover Indicator */}
                                <div className={`hidden absolute top-6 right-6 w-10 h-10 bg-white rounded-full flex items-center justify-center transition-all duration-500 ${isHovered ? 'opacity-100 scale-100' : 'opacity-0 scale-0'
                                    }`}>
                                    <svg
                                        className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1"
                                        fill="none"
                                        viewBox="0 0 14 10"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="1.5"
                                            d="M1 5h12m0 0L9 1m4 4L9 9"
                                        />
                                    </svg>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default Projects;
